import { useState } from "react";

import { IAddEmailResponse } from "@/Common/Api/AsyncActions/EmployeeAsyncActions";
import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { ISaveEmailOptions } from "@/Common/Models/Employee/ISaveEmailOptions";

/**
 * The options to apply when initializing the hook.
 */
interface IUseEmailUpdateStateParams {
    /**
     * The email addresses currently saved in the backend.
     */
    emails: Email[];

    /**
     * Whether the user should receive notifications through the selected email.
     */
    isNotifiedByEmail: boolean;
}

interface IEmailUpdateState {
    /**
     * The email being modified.
     */
    pendingEmail: ISaveEmailOptions;

    /**
     * The email currently saved in the backend, if any.
     */
    savedEmail: IAddEmailResponse | null;

    /**
     * A callback to set the pending email.
     * @param email The email fields to save.
     * @returns The new email object to track.
     */
    setPendingEmail: React.Dispatch<React.SetStateAction<ISaveEmailOptions>>;

    /**
     * A callback to set the current email in the backend.
     * @param email The email fields to save.
     * @returns The new email object to track.
     */
    setSavedEmail: React.Dispatch<React.SetStateAction<IAddEmailResponse>>;
}

/**
 * A React hook that manages initializing the email being modified and the email currently saved in the backend.
 * @param props The parameters to apply when initializing the state, or `null` to get values from the {@link sessionStore}.
 * @returns An object containing state variables and functions to update email state.
 */
function useEmailUpdateState(props?: IUseEmailUpdateStateParams): IEmailUpdateState {
    // get main email
    const email = props?.emails?.firstOrDefault();
    const isNotifiedByEmail =
        props?.isNotifiedByEmail
        ?? sessionStore.unverifiedEmail?.IsSelectedForNotification
        ?? false;

    // init current email based on props or session store
    const currentEmail =
        email
        ?? (sessionStore.unverifiedEmail
            ? new Email({
                Address: sessionStore.unverifiedEmail.Address ?? "",
                ContactPoint_id: sessionStore.unverifiedEmail.ContactPointId,
                IsOkToContact: sessionStore.unverifiedEmail.IsOkToContact ?? false,
                isSelectedForNotification: isNotifiedByEmail,
                IsVerified: false
            })
            : null
        );

    // State for pending email changes on the front end
    const [pendingEmail, setPendingEmail] = useState<ISaveEmailOptions>(() => {
        const newState = {
            emailAddress: currentEmail?.IsVerified ? "" : (currentEmail?.Address ?? ""),
            contactPointId: currentEmail?.ContactPoint_id,
            isOkToContact: currentEmail?.IsVerified ? false : (currentEmail?.IsOkToContact ?? false),
            isNotifiedByEmail: currentEmail?.IsVerified ? false : isNotifiedByEmail
        };
        return newState;
    });

    // State for saved email in the backend
    const [savedEmail, setSavedEmail] = useState<IAddEmailResponse | null>(() => {
        if (!currentEmail)
            return null;

        return {
            emailAddress: currentEmail?.Address ?? null,
            contactPointId: currentEmail?.ContactPoint_id ?? null,
            isOkToContact: currentEmail?.IsOkToContact ?? null,
            isVerified: currentEmail?.IsVerified ?? false,
            isNotifiedByEmail
        };
    });

    // Return state variables and functions
    return {
        pendingEmail,
        savedEmail,
        setPendingEmail,
        setSavedEmail
    };
}

export { IUseEmailUpdateStateParams, useEmailUpdateState };
